import React, { useState, useEffect, useMemo } from 'react';
import { bgColorClassname, parseInstanceName } from '@/utils';
import { Heading, Highlight, Link } from '@/components';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { fetchTicketData } from '@/utils';
import moment from 'moment-timezone';
import { startOfMonth, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Link as GatsbyLink } from 'gatsby';
import * as Sentry from '@sentry/gatsby';
import { ArrowLeft, CalendarIcon, Clock } from 'lucide-react';

interface TicketingWidgetProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  isBirthdayWidget: boolean;
  title: string;
  includeCallout: boolean;
  callout?: CalloutData;
}

interface CalloutData {
  style: 'coral' | 'royalBlue';
  text: string;
}

interface Event {
  name: string;
  type: string;
  purchaseUrl: string;
  instances: Instance[];
}

interface Instance {
  id: string;
  name: string;
  eventName: string;
  date: string;
  soldOut: boolean;
  saleStatus: string;
  purchaseUrl: string;
  allocations: Allocation[];
}

interface Allocation {
  soldOut: boolean;
  name: string;
}

interface ScheduleEvent {
  id: string;
  eventName: string;
  soldOut: boolean;
  name: string;
  purchaseUrl: string;
  isMemberOnly: boolean;
}

interface ScheduleEventData {
  [key: string]: ScheduleEvent[];
}

const SALESFORCE_FALLBACK_URL = 'https://nationalchildrenmuseum.my.salesforce-sites.com/ticket#/';

const TicketingWidget: React.FC<TicketingWidgetProps> = ({
  title,
  backgroundColor,
  isBirthdayWidget,
  includeCallout,
  callout,
}) => {
  const [step, setStep] = useState<number>(1);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState<string | null>(null);
  const [eventData, setEventData] = useState<Event[]>([]);
  const [eventSlot, setEventSlot] = useState<ScheduleEvent | undefined>(undefined);
  const [scheduleEventData, setScheduleEventData] = useState<ScheduleEventData>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const today = useMemo(() => new Date(), []);
  const [defaultMonth, setDefaultMonth] = useState<Date>(today);

  useEffect(() => {
    const loadTicketData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchTicketData(isBirthdayWidget);
        setEventData(data);
      } catch (error) {
        console.error('Error loading ticket data:', error);
        // Log error to Sentry for tracking
        Sentry.captureException(error, {
          tags: {
            component: 'TicketingWidget',
            action: 'loadTicketData',
            isBirthdayWidget: isBirthdayWidget.toString(),
          },
          extra: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTicketData();
    setStep(1);
  }, [isBirthdayWidget]);

  const processEventData = useMemo(() => {
    const initSchedule: ScheduleEventData = {};

    eventData.forEach((event) => {
      event.instances.forEach((instance) => {
        let isSoldOut = instance.soldOut;
        let isMemberOnly = false;
        // Parse time label and subtitle from instance name
        // This extracts the time (e.g., "9:30 AM") and subtitle (e.g., "Gabby's Dollhouse MEOWseum Discovery Day")
        const { timeLabel, subtitle } = parseInstanceName(instance.name);
        let specialEventName = subtitle;

        // Find the appropriate allocation to use based on the new logic
        let selectedAllocation = null;

        if (isBirthdayWidget) {
          // For birthday widgets, use the first available allocation
          // Birthday events typically have different allocation names
          selectedAllocation = instance.allocations[0];
        } else {
          // For regular tickets, use the priority logic
          // First, try to find Museum Admission allocation, with fallback for dates before cutoff
          const cutoffDate = moment.tz('2025-10-04', 'America/New_York');

          // Always try Museum Admission first
          let museumAdmissionAllocation = instance.allocations.find(
            (allocation) => allocation.name === 'Museum Admission',
          );

          // If before cutoff date and Museum Admission not found, fallback to General Admission
          if (!museumAdmissionAllocation && moment(instance.date).isBefore(cutoffDate)) {
            museumAdmissionAllocation = instance.allocations.find(
              (allocation) => allocation.name === 'General Admission',
            );
          }

          if (museumAdmissionAllocation) {
            selectedAllocation = museumAdmissionAllocation;
          } else {
            // If no Museum Admission, use Member Admission
            const memberAdmissionAllocation = instance.allocations.find(
              (allocation) => allocation.name === 'Member Admission',
            );

            // If the instance name includes 'member', set the sold out status to false and use the instance as the selected allocation
            if (instance.name.toLowerCase().includes('member')) {
              instance.soldOut = false;
              selectedAllocation = instance;
              isMemberOnly = true;
            }

            if (memberAdmissionAllocation) {
              selectedAllocation = memberAdmissionAllocation;
              isMemberOnly = true;
            }
          }
        }

        // If no allocation found, use the instance as the selected allocation so the instance displays as sold out
        if (!selectedAllocation) {
          selectedAllocation = instance;
        }

        // Use the selected allocation's sold out status
        isSoldOut = selectedAllocation.soldOut;

        // Check if the word "member" is in the special event's title to determine if it's member-only
        // This applies to both special event names and regular event names
        const eventTitleToCheck = specialEventName || instance.name || '';
        if (eventTitleToCheck.toLowerCase().includes('member')) {
          isMemberOnly = true;
        }

        if (isBirthdayWidget && instance.eventName.includes('Birthday Reservation')) {
          // For birthday widgets, check all allocations for sold out status
          instance.allocations.forEach((allocation) => {
            if (allocation.soldOut) {
              isSoldOut = true;
            }
          });
        }

        const dateMoment = moment(instance.date);
        const date = dateMoment.tz('America/New_York').format('YYYY-MM-DD');
        // Use parsed time label if available, otherwise fall back to moment.js formatting
        // This ensures consistent time display from the instance name
        const timeClient =
          timeLabel || moment.utc(dateMoment).tz('America/New_York').format('hh:mm a');
        const { id, saleStatus } = instance;

        // Determine the correct purchaseUrl based on allocation type
        let finalPurchaseUrl = instance.purchaseUrl;

        if (!isBirthdayWidget && selectedAllocation.name.toLowerCase().includes('member')) {
          // For Member Admission, use the event-level purchaseUrl
          finalPurchaseUrl = event.purchaseUrl;
        }

        if (!initSchedule[date]) {
          initSchedule[date] = [];
        }

        if (saleStatus === 'On Sale') {
          initSchedule[date].push({
            id,
            eventName:
              isBirthdayWidget || instance.eventName.includes('Soirée')
                ? instance.name
                : specialEventName || '',
            soldOut: isSoldOut,
            name: timeClient,
            purchaseUrl: finalPurchaseUrl,
            isMemberOnly,
          });
        }
      });
    });

    // Sort events and clean up empty days
    Object.keys(initSchedule).forEach((day) => {
      if (!initSchedule[day].length) {
        delete initSchedule[day];
        return;
      }

      initSchedule[day].sort((a: ScheduleEvent, b: ScheduleEvent) => {
        if (a.isMemberOnly !== b.isMemberOnly) {
          return a.isMemberOnly ? -1 : 1;
        }
        return moment(a.name, 'hh:mm a').valueOf() - moment(b.name, 'hh:mm a').valueOf();
      });
    });

    return initSchedule;
  }, [eventData, isBirthdayWidget]);

  useEffect(() => {
    setScheduleEventData(processEventData);
  }, [processEventData]);

  const checkDisableDate = (day: Date) => {
    const dateString = moment(day).format('YYYY-MM-DD');
    const today = moment().startOf('day');
    const selectedDay = moment(day).startOf('day');

    // Disable past dates
    if (selectedDay.isBefore(today)) {
      return true;
    }

    // Temp. Check if it's Tuesday 4/15/2025 (exception date)
    const isExceptionDate =
      selectedDay.year() === 2025 &&
      selectedDay.month() === 3 && // April is month 3 (zero-based)
      selectedDay.date() === 15;

    const isTuesday = selectedDay.day() === 2 && !isExceptionDate;

    // Disable if no event data exists for the selected day
    return !scheduleEventData[dateString] || isTuesday;
  };

  const handleDateSelection = (selectedDate: Date | undefined): void => {
    setDate(selectedDate);
    if (selectedDate) {
      setDefaultMonth(startOfMonth(selectedDate));
    } else {
      setDefaultMonth(today);
    }
    setTimeSlot(null);
    setEventSlot(undefined);
  };

  const handleTimeSlotSelection = (slot: ScheduleEvent): void => {
    setTimeSlot(slot.name);
    setEventSlot(slot);
  };

  const selectedDateFormatted = date ? moment(date).format('YYYY-MM-DD') : '';
  const formatWeekdayName = (date: Date): string => format(date, 'EEEE')[0];

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='font-halyarddisplay mx-auto max-w-screen-xl px-5 py-8 md:flex md:gap-x-6 md:p-[50px] lg:gap-x-12 xl:gap-x-24'>
        <div className='w-full text-lg font-normal xl:text-xl'>
          {title && <Heading title={title} />}
          <Card className='border-0 shadow-none'>
            <CardHeader className='border-b p-6'>
              <div className='relative flex items-center justify-center gap-3'>
                {step > 1 && (
                  <Button
                    onClick={() => setStep(step - 1)}
                    variant='outline'
                    size='sm'
                    className='absolute left-0 z-[1] h-9 w-9 border-zinc-200 sm:h-auto sm:w-auto md:h-9 md:w-9 lg:h-auto lg:w-auto'
                  >
                    <span className='flex items-center gap-2'>
                      <ArrowLeft
                        className='h-5 w-5 sm:h-4 sm:w-4 md:h-5 md:w-5 lg:h-4 lg:w-4'
                        strokeWidth={3}
                        aria-hidden='true'
                      />
                      <span className='hidden sm:block md:hidden lg:block'>Back</span>
                    </span>
                  </Button>
                )}
                <Button
                  onClick={() => setStep(1)}
                  disabled={step < 1}
                  variant='icon'
                  size='icon'
                  className={cn(
                    'disabled:bg-blue-100 disabled:opacity-100',
                    step >= 1 ? 'bg-blue-500' : 'bg-blue-100',
                  )}
                  id='calendar-icon'
                  aria-label='Select date'
                >
                  <CalendarIcon
                    className={cn('h-5 w-5', step >= 1 ? 'text-white' : 'text-blue-500')}
                    aria-hidden='true'
                  />
                </Button>
                <div
                  className='w-10 border-b-2 border-zinc-200'
                  aria-hidden='true'
                ></div>
                <Button
                  onClick={() => setStep(2)}
                  disabled={step < 2}
                  variant='icon'
                  size='icon'
                  className={cn(
                    'disabled:bg-blue-100 disabled:opacity-100',
                    step >= 2 ? 'bg-blue-500' : 'bg-blue-100',
                  )}
                  id='clock-icon'
                  aria-label='Select time'
                >
                  <Clock
                    className={cn('h-5 w-5', step >= 2 ? 'text-white' : 'text-blue-500')}
                    aria-hidden='true'
                  />
                </Button>
              </div>
            </CardHeader>
            {step === 1 && (
              <CardContent className='py-10 lowercase'>
                <div className='flex flex-col items-center gap-y-8'>
                  {eventData?.length > 0 && (
                    <Heading
                      title={isBirthdayWidget ? 'Select a day for your party' : 'Select a day'}
                      isShowCurveLine={false}
                      className='mb-0'
                    />
                  )}
                  {isLoading ? (
                    <div className='flex flex-col items-center gap-2'>
                      <svg
                        className='-ml-1 mr-3 h-5 w-5 animate-spin text-black'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        />
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        />
                      </svg>
                      loading...
                    </div>
                  ) : eventData.length > 0 ? (
                    <>
                      <Calendar
                        mode='single'
                        selected={date}
                        defaultMonth={defaultMonth}
                        onSelect={handleDateSelection}
                        disabled={(day: Date) => checkDisableDate(day)}
                        formatters={{
                          formatWeekdayName,
                        }}
                        id='calendar-widget'
                      />
                      {date && (
                        <Button
                          onClick={() => setStep(2)}
                          variant='default'
                          size='lg'
                          className='font-semibold'
                          id='next-button'
                        >
                          next
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className='text-center text-lg leading-7'>
                      <p className='mb-0'>
                        sorry, we’re unable to load ticket availability at the moment.
                      </p>
                      <p className='mb-4'>
                        please try again later, or click the button below to book your visit
                        directly.
                      </p>
                      <Link
                        externalLink={SALESFORCE_FALLBACK_URL}
                        variant='default'
                        className='font-semibold'
                      >
                        direct ticketing page
                      </Link>
                    </div>
                  )}
                </div>
              </CardContent>
            )}
            {step === 2 && selectedDateFormatted && (
              <CardContent className='py-10 lowercase'>
                <div className='flex flex-col items-center gap-10'>
                  <Heading
                    title='select a time'
                    isShowCurveLine={false}
                    className='mb-0'
                  />
                  <div className='time-slot'>
                    <div className='gap-x-2 pb-4'>
                      {scheduleEventData[selectedDateFormatted]?.map((slot, index) => {
                        const slots = scheduleEventData[selectedDateFormatted] || [];
                        const lastSlot = slots[slots.length - 1];
                        const isLast = lastSlot?.id === slot.id;
                        const closingTime = isLast
                          ? moment(slot.name, 'hh:mm a').add(1, 'hour').format('h:mm a')
                          : '4:30 pm';

                        return (
                          <Button
                            key={slot.id}
                            onClick={() => handleTimeSlotSelection(slot)}
                            disabled={slot.soldOut}
                            className={cn(
                              buttonVariants({ variant: 'outline' }),
                              'focus-visible:ring-none group relative my-2 flex h-auto w-full max-w-[300px] items-center justify-center whitespace-normal py-1 text-lg font-medium leading-7 hover:text-black focus:border-white focus:text-black focus-visible:outline-none focus-visible:ring-offset-0 active:text-black disabled:cursor-not-allowed disabled:text-zinc-400 disabled:opacity-100 sm:w-[300px]',
                              eventSlot?.id === slot.id && 'bg-green-500',
                            )}
                          >
                            <div className='ease-bounce absolute inset-0 flex items-center justify-center overflow-hidden rounded-full border border-zinc-200 transition-all after:absolute after:block after:w-full after:scale-0 after:rounded-full after:bg-green-500 after:pt-[100%] after:opacity-20 after:transition-all group-hover:scale-[1.15] group-hover:after:scale-100 group-hover:after:opacity-100 group-focus:scale-[1.15] group-focus:after:scale-100 group-focus:after:opacity-100 group-active:scale-[1.15] group-active:after:scale-100 group-active:after:opacity-100 group-disabled:group-hover:scale-100 group-disabled:group-hover:after:scale-100 group-disabled:group-hover:after:bg-white group-disabled:group-hover:after:transition-none' />
                            <div className='relative'>
                              {slot.name}
                              {slot.eventName && (
                                <div className='pb-0.5 text-sm font-normal leading-5 text-zinc-600'>
                                  {slot?.eventName ? slot.eventName : 'member magic hour'}
                                  {slot.isMemberOnly && (
                                    <span className='ml-1 inline-block'>
                                      <svg
                                        width='9'
                                        height='8'
                                        viewBox='0 0 9 8'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                        aria-hidden='false'
                                        aria-label='Members-only event'
                                      >
                                        <path
                                          d='M4.41813 0.261393L4.56181 0.922581C4.69634 1.54088 5.40652 1.83502 5.93879 1.49283L6.50811 1.12696C6.80811 0.934066 7.15968 1.28564 6.96679 1.58564L6.60092 2.15495C6.25872 2.68723 6.55287 3.39741 7.17117 3.53194L7.83236 3.67562C8.18088 3.75132 8.18088 4.24868 7.83236 4.32438L7.17117 4.46806C6.55287 4.60259 6.25872 5.31277 6.60092 5.84504L6.96679 6.41436C7.15968 6.71436 6.80811 7.06593 6.50811 6.87304L5.93879 6.50717C5.40652 6.16497 4.69634 6.45912 4.56181 7.07742L4.41813 7.73861C4.34243 8.08713 3.84507 8.08713 3.76937 7.73861L3.62569 7.07742C3.49116 6.45912 2.78098 6.16497 2.2487 6.50717L1.67939 6.87304C1.37939 7.06593 1.02782 6.71436 1.22071 6.41436L1.58658 5.84504C1.92878 5.31277 1.63463 4.60259 1.01633 4.46806L0.355143 4.32438C0.00661898 4.24868 0.00661898 3.75132 0.355143 3.67562L1.01633 3.53194C1.63463 3.39741 1.92878 2.68723 1.58658 2.15495L1.22071 1.58564C1.02782 1.28564 1.37939 0.934066 1.67939 1.12696L2.2487 1.49283C2.78098 1.83502 3.49116 1.54088 3.62569 0.922581L3.76937 0.261393C3.84507 -0.087131 4.34243 -0.087131 4.41813 0.261393Z'
                                          fill='#FC9997'
                                        />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              )}
                              {!isBirthdayWidget && isLast && (
                                <div className='text-sm font-normal leading-5 text-zinc-600'>
                                  museum closes at {closingTime}
                                </div>
                              )}
                            </div>
                          </Button>
                        );
                      })}
                    </div>
                    {scheduleEventData[selectedDateFormatted]?.some(
                      (slot) => slot.isMemberOnly,
                    ) && (
                      <div className='flex w-full max-w-[300px] justify-center gap-1 sm:w-[300px]'>
                        <div className='mt-1'>
                          <svg
                            width='11'
                            height='10'
                            viewBox='0 0 11 10'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                            aria-hidden='false'
                            aria-label='Members-only event'
                          >
                            <path
                              d='M5.49923 0.326741L5.67882 1.15323C5.84699 1.9261 6.73471 2.29378 7.40006 1.86604L8.11169 1.4087C8.4867 1.16758 8.92617 1.60705 8.68505 1.98205L8.22771 2.69369C7.79997 3.35904 8.16765 4.24676 8.94052 4.41493L9.76701 4.59452C10.2027 4.68915 10.2027 5.31085 9.76701 5.40548L8.94052 5.58507C8.16765 5.75324 7.79997 6.64096 8.22771 7.3063L8.68505 8.01794C8.92617 8.39295 8.4867 8.83242 8.11169 8.5913L7.40006 8.13396C6.73471 7.70622 5.84699 8.0739 5.67882 8.84677L5.49923 9.67326C5.4046 10.1089 4.7829 10.1089 4.68827 9.67326L4.50868 8.84677C4.34051 8.0739 3.45279 7.70622 2.78744 8.13396L2.0758 8.5913C1.7008 8.83242 1.26133 8.39295 1.50245 8.01794L1.95979 7.3063C2.38753 6.64096 2.01985 5.75324 1.24698 5.58507L0.420491 5.40548C-0.0151638 5.31085 -0.0151638 4.68915 0.420491 4.59452L1.24698 4.41493C2.01985 4.24676 2.38753 3.35904 1.95979 2.69369L1.50245 1.98205C1.26133 1.60705 1.7008 1.16758 2.0758 1.4087L2.78744 1.86604C3.45279 2.29378 4.34051 1.9261 4.50868 1.15323L4.68827 0.326741C4.7829 -0.108914 5.4046 -0.108914 5.49923 0.326741Z'
                              fill='#FC9997'
                            />
                          </svg>
                        </div>
                        <div className='text-xs font-normal leading-4 text-zinc-600'>
                          <div>
                            Want to become a member to enjoy exclusive access, benefits, and savings
                            on future visits?
                          </div>
                          <div>
                            Head to our{' '}
                            <GatsbyLink
                              to='/visit/membership'
                              className='font-bold underline'
                            >
                              membership page
                            </GatsbyLink>{' '}
                            to learn more!
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {timeSlot && (
                    <Link
                      externalLink={eventSlot ? eventSlot?.purchaseUrl : '#'}
                      variant='default'
                    >
                      {isBirthdayWidget || eventSlot?.isMemberOnly ? `reserve now` : `purchase now`}
                    </Link>
                  )}
                </div>
              </CardContent>
            )}
            {(date || timeSlot) && (
              <CardFooter className='flex justify-center border-t p-5'>
                <div
                  id='ticket-attribute-summary'
                  className='flex flex-col items-center justify-center gap-3 lowercase sm:flex-row md:flex-col lg:flex-row'
                >
                  <div className='text-base font-bold'>details</div>
                  {isBirthdayWidget && (
                    <>
                      <div className='w-10 border-b-2 border-zinc-200'></div>
                      <div className='text-base'>birthday party booking</div>
                    </>
                  )}
                  {date && (
                    <>
                      <div className='h-2 w-2 rounded-full bg-teal-500'></div>
                      <div className='text-base'>{moment(date).format('MMMM DD, YYYY')}</div>
                    </>
                  )}
                  {timeSlot && (
                    <>
                      <div className='h-2 w-2 rounded-full bg-teal-500'></div>
                      <div className='text-base'>{timeSlot}</div>
                    </>
                  )}
                </div>
              </CardFooter>
            )}
          </Card>
          {includeCallout && callout && (
            <Highlight
              highlightText={callout.text}
              icon={callout.style}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketingWidget;
